import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import DictionaryButton from "./DictionaryButton";
import ProfileButton from "./ProfileButton";
import { NavigateFunction } from "react-router-dom";
import { getPlatform } from "@/lib/iap";
import CoursesButton from "./CoursesButton";
import HomeButton from "./HomeButton";
import FeedButton from "./FeedButton";
import { useReviewCount } from "@/hooks/useReviewCount";
import { NavVariantContext } from "./NavVariantContext";
import { useStreakCalendar } from "../Streak/StreakCalendarContext";
export type ActivePage =
  | "learn"
  | "dictionary"
  | "profile"
  | "courses"
  | "feed";

interface NavBarProps {
  activePage: ActivePage;
  navigate: NavigateFunction;
  /**
   * Bottom-nav color scheme. "light" is the default frosted bar; "dark" is the
   * black variant used on the feed / locked-feed (iOS parity). The transition
   * between the two animates via CSS transition-colors.
   */
  variant?: "light" | "dark";
}

// Left→right tab order (must match the button order rendered below).
const TAB_ORDER: ActivePage[] = [
  "learn",
  "courses",
  "feed",
  "dictionary",
  "profile",
];

// Selection highlight behind the active icon — a large rounded capsule that
// fills most of the tab. The whole bar is only 56px tall, so we size the pill
// to nearly fill it (a prominent highlight, not a small crushed oval) and clamp
// both axes so it stays big even on tiny tabs. WIDTH is a high proportion of the
// tab width (clamped); HEIGHT has a generous floor so it never collapses on
// narrow phones (this was the bug — the pill was barely taller than the icon).
// WIDTH is kept a touch NARROWER than a tab (ratio < 1, modest min) so the edge
// tabs (Home / Profile) can center the pill on their icon without hitting the
// clamp — a tab-wide pill overflows the bar's rounded end, gets clamped inward,
// and reads as "bleeding" toward the middle on one side only. Slightly narrower
// = symmetric on every tab. HEIGHT is a moderate horizontal oval (not too tall).
const PILL_W_RATIO = 0.96;
const PILL_W_MIN = 67;
const PILL_W_MAX = 92;
const PILL_H_MIN = 44;
const PILL_H_MAX = 52;
const PILL_H_RATIO = 0.72;
const PILL_Y_OFFSET = 10;
const PILL_EDGE_INSET = 4;

// iOS dark-nav variant (feed / locked-feed) — mirrors the iOS 26 "Liquid Glass"
// dark tab bar: a LIGHTER translucent dark material (not pure black) over the
// backdrop blur, white-outline inactive icons (see NavButton), blue active icon,
// and a subtle LIGHT selection pod (vs the dark pod on the light bar).
const DARK_BAR_BG = "rgba(38,38,42,0.76)"; // translucent dark glass, not #000
const DARK_PILL_BG = "rgba(255,255,255,0.18)"; // light pod, visible on the dark bar
const LIGHT_PILL_BG = "rgba(60,60,67,0.18)"; // original gray pod on the light bar

// Module-level so the circle's last x survives a NavBar REMOUNT. HomeLayout now
// keeps a SINGLE NavBar instance mounted across the Feed boundary (so the
// light↔dark variant can tween in-place — see #4), so in normal tab switching
// the pill slides via the in-mount spring and this is unused. It remains as a
// safety net: if the NavBar is ever genuinely remounted (e.g. a future route
// change that re-creates this subtree), we seed the new circle's `initial` with
// this saved x so it animates from the previous tab's position instead of
// snapping.
let savedCircleX: number | null = null;

const NavBar: React.FC<NavBarProps> = ({
  activePage,
  navigate,
  variant = "light",
}) => {
  const isDark = variant === "dark";
  const platform = getPlatform();
  const reviewCount = useReviewCount();
  const activeIndex = Math.max(0, TAB_ORDER.indexOf(activePage));

  // Close the streak calendar overlay (if open) before switching tabs so it
  // never lingers over the next page. No-op when rendered outside the provider
  // (e.g. the feed bar). Event-driven on purpose — avoids a route-change effect.
  const { close: closeCalendar } = useStreakCalendar();
  const go = React.useCallback(
    (path: string) => {
      closeCalendar();
      navigate(path);
    },
    [closeCalendar, navigate],
  );

  const rowRef = useRef<HTMLDivElement>(null);
  // Active tab geometry (relative to the row): icon center + the tab's width +
  // the row's total width, so the oval can size proportionally, sit behind the
  // icon, and be clamped so the edge tabs (Home/Profile) don't bleed past the bar.
  const [center, setCenter] = useState<{
    x: number;
    y: number;
    w: number;
    rowW: number;
  } | null>(null);
  const scaleControls = useAnimationControls();
  const firstRender = useRef(true);
  // The circle's x at the moment THIS NavBar mounted (the previous tab's x, if
  // we just remounted across the Feed boundary). Captured once.
  const mountX = useRef(savedCircleX);

  // Measure the active icon's center so the circle sits exactly behind it and
  // glides to the right place at any width.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const buttons = row.querySelectorAll("button");
      const btn = buttons[activeIndex] as HTMLElement | undefined;
      if (!btn) return;
      const iconEl =
        (btn.querySelector("[data-nav-icon]") as HTMLElement | null) ?? btn;
      const rowRect = row.getBoundingClientRect();
      const iconRect = iconEl.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      setCenter({
        x: iconRect.left - rowRect.left + iconRect.width / 2,
        y: iconRect.top - rowRect.top + iconRect.height / 2,
        w: btnRect.width,
        rowW: rowRect.width,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [activeIndex]);

  // Keep the module-level x in sync so a later remount can slide from here.
  useEffect(() => {
    if (center) savedCircleX = center.x;
  }, [center]);

  const pulse = React.useCallback(() => {
    scaleControls.start({
      scale: [1, 1.28, 1],
      transition: { duration: 0.4, times: [0, 0.45, 1], ease: "easeInOut" },
    });
  }, [scaleControls]);

  // Pulse on each in-mount selection change (grow → shrink while it glides).
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    pulse();
  }, [activeIndex, pulse]);

  // If this NavBar just remounted across the Feed boundary AND we have a prior
  // x, the circle slides in (via `initial` below) — pulse to match.
  useEffect(() => {
    if (mountX.current != null) pulse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Oval sized as a PROPORTION of the tab width (clamped) so it stays
  // well-sized on narrow phones instead of collapsing; height tracks width
  // (capped) so it stays a horizontal oval.
  const pillW = center
    ? Math.min(PILL_W_MAX, Math.max(PILL_W_MIN, Math.round(center.w * PILL_W_RATIO)))
    : 0;
  const pillH = Math.min(
    PILL_H_MAX,
    Math.max(PILL_H_MIN, Math.round(pillW * PILL_H_RATIO))
  );
  // Clamp the pill so the edge tabs (Home / Profile) don't bleed past the bar's
  // rounded ends — the wide pill on a narrow edge tab would otherwise overflow.
  const clampLeft = (rawLeft: number) =>
    center
      ? Math.max(
          PILL_EDGE_INSET,
          Math.min(center.rowW - pillW - PILL_EDGE_INSET, rawLeft)
        )
      : rawLeft;
  const pillLeft = center ? clampLeft(center.x - pillW / 2) : 0;

  return (
    <NavVariantContext.Provider value={variant}>
      <div
        className={`fixed bottom-0 left-0 right-0 flex justify-center ${
          platform === "web" ? "bottom-padding-web" : "pb-5"
        }`}
      >
      {/* iOS order (left->right): Home · Courses · Feed (center) · Dictionary · Profile.
          Floating frosted rounded-capsule tab bar, inset from the edges and
          centered — matches the iOS liquid-glass NativeTabs bar. */}
      <div
        ref={rowRef}
        // bg/border swap between the light frosted bar and the iOS dark feed
        // bar; transition-colors animates the swap (500ms) instead of a hard
        // flip. The bar background is driven via inline style so it tweens.
        className={`relative flex justify-evenly items-center w-[calc(100%-3rem)] max-w-[480px] mx-auto rounded-full backdrop-blur-xl border shadow-[0_8px_24px_rgba(0,0,0,0.12)] px-1 transition-colors duration-500 ease-in-out ${
          isDark ? "border-white/10" : "border-white/40"
        }`}
        style={{
          backgroundColor: isDark ? DARK_BAR_BG : "rgba(255,255,255,0.75)",
        }}
      >
        {/* Light-gray selection circle behind the active icon. The outer layer
            glides horizontally (spring) to the active tab; the inner layer
            pulses scale on each selection change (grow mid-move, shrink on
            arrival) — the iOS liquid-glass selection feel. */}
        {center && (
          <motion.div
            className="absolute z-10 pointer-events-none"
            style={{
              width: pillW,
              height: pillH,
              top: center.y - pillH / 2 + PILL_Y_OFFSET,
            }}
            // Slide in from the prior tab's x on a remount (Feed boundary);
            // otherwise just appear in place. In-mount tab switches animate via
            // the changing `animate` target.
            initial={
              mountX.current != null
                ? { left: clampLeft(mountX.current - pillW / 2) }
                : false
            }
            animate={{ left: pillLeft }}
            transition={{ type: "spring", stiffness: 430, damping: 32 }}
          >
            <motion.div
              className="w-full h-full rounded-full"
              // Light pod on the dark feed bar, gray pod on the light bar;
              // tween the shade over 500ms to match the bar's glassy swap.
              style={{
                background: isDark ? DARK_PILL_BG : LIGHT_PILL_BG,
                transition: "background-color 500ms ease-in-out",
              }}
              animate={scaleControls}
            />
          </motion.div>
        )}

        <HomeButton
          isActive={activePage === "learn"}
          onClick={() => go("/home/learn")}
        />
        <CoursesButton
          isActive={activePage === "courses"}
          onClick={() => go("/home/courses")}
        />
        <FeedButton
          isActive={activePage === "feed"}
          onClick={() => go("/home/feed")}
        />
        <DictionaryButton
          isActive={activePage === "dictionary"}
          reviewCount={reviewCount}
          onClick={() => go("/home/dictionary")}
        />
        <ProfileButton
          isActive={activePage === "profile"}
          onClick={() => {
            go("/home/profile");
          }}
        />
      </div>
      </div>
    </NavVariantContext.Provider>
  );
};

export default NavBar;
